import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Button } from "./Button";

/**
 * Admin compose + manage panel for workspace announcements (Settings →
 * Workspace). Shows the read-receipt count per announcement.
 */
export function AnnouncementAdmin() {
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [pinned, setPinned] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");
  const [teamId, setTeamId] = useState("");

  const q = useQuery({
    queryKey: queryKeys.announcements(),
    queryFn: api.announcements,
  });
  const teamsQ = useQuery({ queryKey: ["teams"], queryFn: () => api.teams() });
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: queryKeys.announcements() });

  const create = useMutation({
    mutationFn: () =>
      api.createAnnouncement({
        title: title.trim(),
        body: body.trim() || undefined,
        pinned,
        expiresAt: expiresAt || null,
        teamId: teamId || null,
      }),
    onSuccess: () => {
      setTitle("");
      setBody("");
      setPinned(false);
      setExpiresAt("");
      setTeamId("");
      invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteAnnouncement(id),
    onSuccess: invalidate,
  });

  const items = q.data?.announcements ?? [];

  return (
    <div className="anc-admin">
      <h3 className="anc-admin-h">Announcements</h3>
      <form
        className="anc-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (title.trim()) create.mutate();
        }}
      >
        <input
          className="input"
          placeholder="Announcement title…"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          aria-label="Announcement title"
        />
        <textarea
          className="input"
          placeholder="Message (optional)…"
          value={body}
          rows={2}
          onChange={(e) => setBody(e.target.value)}
          aria-label="Announcement body"
        />
        <div className="anc-form-row">
          <label className="anc-check">
            <input
              type="checkbox"
              checked={pinned}
              onChange={(e) => setPinned(e.target.checked)}
            />
            Pin
          </label>
          <label className="anc-check">
            Expires
            <input
              type="date"
              className="input"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              aria-label="Expiry date"
              style={{ width: 150 }}
            />
          </label>
          <select
            className="select"
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
            aria-label="Audience"
            style={{ width: 160 }}
          >
            <option value="">Whole workspace</option>
            {(teamsQ.data?.teams ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                Team: {t.name}
              </option>
            ))}
          </select>
          <Button
            variant="primary"
            type="submit"
            disabled={!title.trim() || create.isPending}
          >
            Post
          </Button>
        </div>
      </form>

      {items.length > 0 && (
        <ul className="anc-admin-list">
          {items.map((a) => (
            <li key={a.id} className="anc-admin-item">
              <span className="anc-admin-title">
                {a.pinned ? "📌 " : ""}
                {a.title}
              </span>
              <span className="t-sm t-muted">
                {a.teamName ? `Team: ${a.teamName}` : "Workspace"} ·{" "}
                {a.readCount} read{a.readCount === 1 ? "" : "s"}
                {a.expiresAt ? ` · expires ${a.expiresAt}` : ""}
              </span>
              <button
                type="button"
                className="link-btn"
                onClick={() => remove.mutate(a.id)}
                aria-label={`Delete announcement ${a.title}`}
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
